import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, PRODUCT_TYPE_LIMITED_TIME_SPECIAL } from 'constants/offer'
import { isBedbankUniqueStaysType, isFlashHotelType } from 'lib/offer/offerTypes'

export type AccommodationSearchTileKind = 'standard' | 'grouped'

/**
 * The product type search presents an accommodation offer as.
 *
 * A property running a dynamic Flash campaign promotion merchandises like a Flash offer, so its
 * tile carries the Limited Time LUX Exclusive badge whatever the offer's own product type, and
 * ahead of a tactical rate's limited time special, as the offer page does.
 */
export function getAccommodationSearchProductType(
  productType: App.ProductType,
  hasEnabledTacticalRate: boolean,
  hasDynamicFlashPromotion?: boolean,
): App.ProductType
export function getAccommodationSearchProductType(
  productType: App.ProductType | undefined,
  hasEnabledTacticalRate: boolean,
  hasDynamicFlashPromotion?: boolean,
): App.ProductType | undefined
export function getAccommodationSearchProductType(
  productType: App.ProductType | undefined,
  hasEnabledTacticalRate: boolean,
  hasDynamicFlashPromotion = false,
): App.ProductType | undefined {
  if (hasDynamicFlashPromotion) return OFFER_TYPE_HOTEL
  return hasEnabledTacticalRate ? PRODUCT_TYPE_LIMITED_TIME_SPECIAL : productType
}

export interface AccommodationSearchPresentationInput {
  offerType?: App.OfferType
  productType?: App.ProductType
  metadataHidePricing?: boolean
  hideAnytimePrices: boolean
  hasStandardOccupancy: boolean
  /**
   * Whether the offer's property is running a dynamic Flash campaign promotion, which merchandises
   * like a Flash offer and so keeps the anytime price visible.
   */
  hasDynamicFlashPromotion?: boolean
}

export interface AccommodationSearchPresentationContext extends AccommodationSearchPresentationInput {
  propertyType?: App.BedbankOffer['propertyType']
  isAnytimeSearch: boolean
  canSelectDates: boolean
  tileKind?: AccommodationSearchTileKind
}

export interface AccommodationSearchActionInput {
  offerType?: App.OfferType
  propertyType?: App.BedbankOffer['propertyType']
  hidePrice: boolean
  isAnytimeSearch: boolean
  canSelectDates: boolean
  tileKind?: AccommodationSearchTileKind
}

export function shouldHideAccommodationSearchPrice({
  offerType,
  productType,
  metadataHidePricing = false,
  hideAnytimePrices,
  hasStandardOccupancy,
  hasDynamicFlashPromotion = false,
}: AccommodationSearchPresentationInput): boolean {
  if (metadataHidePricing) return true

  const isLimitedTimeSpecialWithStandardOccupancy =
    productType === PRODUCT_TYPE_LIMITED_TIME_SPECIAL && hasStandardOccupancy

  return hideAnytimePrices &&
    !isFlashHotelType(offerType) &&
    !isLimitedTimeSpecialWithStandardOccupancy &&
    !hasDynamicFlashPromotion
}

export type AccommodationSearchPrimaryAction = 'view-offer' | 'select-dates'

export interface AccommodationSearchPresentation {
  hidePrice: boolean
  primaryAction: AccommodationSearchPrimaryAction
  forceDateSelection: boolean
}

export interface AccommodationSearchDateSelectionInput {
  offerType?: App.OfferType
  propertyType?: App.BedbankOffer['propertyType']
  isAnytimeSearch: boolean
  canSelectDates: boolean
}

export function shouldForceAccommodationDateSelection({
  offerType,
  propertyType,
  isAnytimeSearch,
  canSelectDates,
}: AccommodationSearchDateSelectionInput): boolean {
  return canSelectDates && isAnytimeSearch && isBedbankUniqueStaysType(offerType, propertyType)
}

export function getAccommodationSearchPrimaryAction({
  offerType,
  propertyType,
  hidePrice,
  isAnytimeSearch,
  canSelectDates,
  tileKind = 'standard',
}: AccommodationSearchActionInput): AccommodationSearchPrimaryAction {
  if (shouldForceAccommodationDateSelection({ offerType, propertyType, isAnytimeSearch, canSelectDates })) {
    return 'select-dates'
  }

  if (!isAnytimeSearch && (offerType === OFFER_TYPE_BED_BANK || tileKind === 'grouped')) {
    return 'view-offer'
  }

  return hidePrice && canSelectDates ? 'select-dates' : 'view-offer'
}

export function getAccommodationSearchPresentation(
  input: AccommodationSearchPresentationContext,
): AccommodationSearchPresentation {
  const hidePrice = shouldHideAccommodationSearchPrice(input)
  const forceDateSelection = shouldForceAccommodationDateSelection({
    offerType: input.offerType,
    propertyType: input.propertyType,
    isAnytimeSearch: input.isAnytimeSearch,
    canSelectDates: input.canSelectDates,
  })

  return {
    hidePrice,
    primaryAction: getAccommodationSearchPrimaryAction({
      offerType: input.offerType,
      propertyType: input.propertyType,
      hidePrice,
      isAnytimeSearch: input.isAnytimeSearch,
      canSelectDates: input.canSelectDates,
      tileKind: input.tileKind,
    }),
    forceDateSelection,
  }
}
